'use client';

import React, { useMemo } from 'react';
import { useGetVolumeByLocationQuery } from 'src/api/redux';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { buildTopProvidersQueryParams } from 'src/components/Profiles/shared/widgets/CompactTopProviders/utils';

const MAX_ITEMS = 3;
const TOP_LOCATION_THRESHOLD = 20;

const CompactVolumeByLocationPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const filterSet = widgetConfig?.filterSet ?? null;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const providerFilterSetId = widgetConfig?.providerFilterSetId;
  const encounterId = widgetConfig?.widget?.encounter?.id ?? null;

  const queryParams = useMemo(
    () => buildTopProvidersQueryParams(filterSet, minYear, maxYear, providerFilterSetId, encounterId),
    [filterSet, minYear, maxYear, providerFilterSetId, encounterId]
  );

  const { data, isLoading } = useGetVolumeByLocationQuery(
    { providerId, queryParams },
    { skip: !providerId || !profileType }
  );

  const topLocations = useMemo(() => {
    if (!data?.results) return [];
    return data.results
      .filter(
        (loc: Record<string, any>) =>
          loc.provider_id && loc.percentage >= TOP_LOCATION_THRESHOLD
      )
      .slice(0, MAX_ITEMS);
  }, [data]);

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-volume-by-location-preview-loading" aria-label="Loading locations" />;
  }

  if (topLocations.length === 0) {
    return <WidgetEmpty data-testid="compact-volume-by-location-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-volume-by-location-preview"
      role="list"
      aria-label="Top Locations"
    >
      {topLocations.map((loc: Record<string, any>, index: number) => (
        <div
          key={`${loc.provider_id}-${index}`}
          className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-b-0"
          role="listitem"
        >
          <span className="text-sm font-medium text-primary truncate">
            {loc.name || 'Unknown'}
          </span>
          <span className="text-sm font-semibold text-teal-900 tabular-nums flex-shrink-0 ml-4">
            {loc.percentage != null ? `${loc.percentage}%` : '-'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CompactVolumeByLocationPreview;
